<template lang="pug">
    .play-box
        template(v-if='!isLoading')
                game-header(
                    v-if="!isBattle"
                    @soundStateChanged ="headerKey = !headerKey"
                    :key="headerKey"
                    :slug='type',
                    :game-type='parameters && parameters.courseType',
                    :is-homework="!!exerciseId",
                )
                .play-box__game-content(v-if='gameStarted && !gameFinished')
                    answer-container(
                        :parameters="parameters"
                        :example.sync="example"
                        :left-column-data.sync="leftColumnData"
                        :central-column-data.sync="centralColumnData"
                        :right-column-data.sync="rightColumnData"
                    )
                    .game-info__block
                        game-info(
                            :score="{left: answers.good, right: answers.bad}"
                            :time="getTime"
                            :slug="type"
                            :parameters="parameters"
                            :game-allows-tips="parameters.isHintEnabled"
                            :opponent="opponent"
                            :opponent-numbers="opponentNumbers"
                            :is-opponent-answering="isOpponentAnswering"
                            :is-from-battle="isBattle"
                            :is-olympiad="isOlympiad"
                        )
                        cs-button.answer-button(
                                color="blue",
                                pattern="primary-outline",
                                type='button',
                                :class="{'answer-button__mt': isBattle}"
                                @click='(isCorrectAnswerChosen ? nextExample : processReply)()',
                            )
                                | {{ $i18n.t(isCorrectAnswerChosen ? 'trainer.trainer_gamePlayBox_further' : 'trainer.trainer_gamePlayBox_reply') }}
                .play-box__end-container(v-if='gameFinished && !isOlympiad')
                    fireworks(:is-mobile="isMobile")
                    .play-box__reaction-container
                        .play-box__answers.play-box__answers--left
                            | {{ $i18n.t('trainer.trainer_game_wordField_yourTime') }}
                            br
                            | {{ getTime }}
                        result-image.play-box__image(status="success")
                        .play-box__answers.play-box__answers--right
                            | {{ $i18n.t('dashboard.dashboard_sidebar_statistics') }}
                            br
                            | {{ answers.good }} / {{ answers.bad }}
                    result-buttons(
                        v-if="!isBattle"
                        :exercise-id="exerciseId"
                        :games-type="parameters.courseType"
                        show-restart-button
                        @restartGame="restartGame"
                    )
                        slot
                calculating-answers(v-if="calculatingAnswers" :is-last-game-in-battle="isLastGameInBattle")
        cs-loader(v-else)
</template>

<script>
    /*eslint-disable*/
    import playBoxGameMixin from '../../../../utils/trainer/playBoxGameMixin.js';
    import { cloneDeep, shuffle } from 'lodash';
    import CsButton from '@/elements/CsButton.vue';
    import CsLoader from '@/elements/CsLoader.vue';
    import AnswerContainer from './AnswerContainer.vue';
    import lettersModes from '../../../../config/games/partsOfSpeech';
    import olympiadRussianLanguageAnswerTimerMixin from '../../../../utils/olympiads/olympiadRussianLanguageAnswerTimerMixin.js';
    import Battle from '../../../../utils/battle/battleMixin';
    import CalculatingAnswers from '../../CalculatingAnswersModal.vue';

    function DEFAULT_DATA() {
        return {
            example: [],
            currentLevel: 0,
            isCorrectAnswerChosen: false,
            answers: { good: 0, bad: 0 },
            leftColumnData: [],
            centralColumnData: [],
            rightColumnData: [],
        };
    }

    export default {
        components: {
            AnswerContainer,
            CsLoader,
            CsButton,
            CalculatingAnswers,
        },
        mixins: [playBoxGameMixin, olympiadRussianLanguageAnswerTimerMixin, Battle],
        data() {
            return DEFAULT_DATA();
        },
        mounted() {
            document.addEventListener('keydown', this.handleKeyPress);
        },
        beforeDestroy() {
            document.removeEventListener('keydown', this.handleKeyPress);
        },
        computed: {
            isInfiniteAmountOfExamples() {
                return this.parameters.infinityAmountOfExamples;
            },
        },
        methods: {
            handleKeyPress(event) {
                if (event.key === 'Enter' && !this.gameFinished) {
                    if (!this.isCorrectAnswerChosen) {
                        return this.processReply();
                    };
                    if (this.isCorrectAnswerChosen) {
                        return this.nextExample();
                    };
                }
            },
            registerSounds() {
                this.$createjs.Sound.removeAllSounds();
                this.$createjs.Sound.removeAllEventListeners();
                this.$createjs.Sound.alternateExtensions = ['mp3', 'ogg'];
                this.$createjs.Sound.registerSounds(this.soundObjects);
            },
            async fetchData() {
                try {
                    await this.getExercise();
                    this.generateExample();
                    this.startGame();
                    this.registerSounds();
                    this.isLoading = false;
                } catch (e) {
                    this.isLoading = false;
                    this.$toasted.error(this.$root.$t('errors.getting_level'));
                    this.$router.push({ name: 'dashboard' });
                }
            },
            refreshGameStatBeforeSend() {
                this.gameStat.answers = this.answers.good;
                this.gameStat.amountOfMistakes = this.answers.bad;
                this.gameStat.attempts = this.parameters.amountOfExamples;
                this.gameStat.isCompleted = this.gameFinished;
                this.gameStat.executionTime = this.time;
                this.gameStat.isCorrect = true;
                this.gameStat.percent = this.calculatePercent();
                this.gameStat.amountOfExamples = this.parameters.amountOfExamples;
                this.gameStat.parameters = cloneDeep(this.parameters);
            },
            startGame() {
                this.gameStarted = true;
                this.startTimer();
            },
            startTimer() {
                this.startAnswerTimer();
                this.$nextTick(() => {
                    if (!this.timer) {
                        this.setTimer();
                    }
                });
            },
            processReply(finishExample = false) {
                this.checkAnswer('verbs', this.leftColumnData);
                this.checkAnswer('nouns', this.rightColumnData);
                this.checkAnswer('adjectives', this.centralColumnData);
                if (!this.example.length) {
                    this.isCorrectAnswerChosen = true;
                }
                if (this.isOlympiad || this.isBattle || finishExample) {
                    this.nextExample();
                }
            },
            nextExample() {
                this.stopAnswerTimer();
                if (!this.isInfiniteAmountOfExamples && this.currentLevel === this.parameters.amountOfExamples - 1) {
                    this.finishGame();
                    return;
                }
                this.isCorrectAnswerChosen = false;
                this.generateExample();
                this.leftColumnData = [];
                this.rightColumnData = [];
                this.centralColumnData = [];
                this.currentLevel++;
                this.startAnswerTimer();
            },
            finishGame() {
                this.gameFinished = true;
                this.stopTimer();
                this.soundInstance = this.$createjs.Sound.play('gameDone');
                this.sendStat();
            },
            calculatePercent() {
                let total = this.answers.good + this.answers.bad;
                if (total > 0) {
                    return parseInt((this.answers.good / total) * 100);
                }
                return 0;
            },
            beforeGameRestart() {
                Object.assign(this, DEFAULT_DATA());
            },
            generateExample() {
                const shuffleFirstModeArray = shuffle(lettersModes['verbs']);
                const shuffleSecondModeArray = shuffle(lettersModes['nouns']);
                const shuffleThirdModeArray = shuffle(lettersModes['adjectives']);
                const shuffleFirstModeArrayLimit = Array(this.parameters.amountOfPairs)
                    .fill(null)
                    .map((el, index) => shuffleFirstModeArray[index % shuffleFirstModeArray.length]);
                const shuffleSecondModeArrayLimit = Array(this.parameters.amountOfPairs)
                    .fill(null)
                    .map((el, index) => shuffleSecondModeArray[index % shuffleSecondModeArray.length]);
                const shuffleThirdArrayLimit = Array(this.parameters.amountOfPairs)
                    .fill(null)
                    .map((el, index) => shuffleThirdModeArray[index % shuffleThirdModeArray.length]);
                this.example = shuffle([...shuffleFirstModeArrayLimit, ...shuffleSecondModeArrayLimit, ...shuffleThirdArrayLimit]).map(value => ({
                    value,
                    disabled: false,
                }));
            },
            checkAnswer(mode, columnData) {
                const columnDataRightSet = new Set(lettersModes[mode]);
                const indexesForReplace = [];
                for (const index in columnData) {
                    const letter = columnData[index];
                    if (letter.disabled) {
                        continue;
                    }
                    if (columnDataRightSet.has(letter.value)) {
                        letter.disabled = true;
                        this.answers.good++;
                    } else {
                        indexesForReplace.unshift(index);
                        this.example.unshift(letter);
                        this.answers.bad++;
                    }
                }
                indexesForReplace.forEach(index => {
                    columnData.splice(index, 1);
                });
            },
        },
    };
</script>

<style lang="scss" scoped>
    @import '@/assets/sass/games/playbox.scss';

    .mobile-version {
        flex-direction: column;
        align-items: center;
        text-align: center;
    }

    .play-box__end-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 100%;
    }

    .play-box__reaction-container {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        width: 100%;
        @include media($md) {
            flex-direction: row;
        }
    }

    .play-box__answers {
        font-weight: 500;
        font-size: 22px;
        line-height: 26px;
        text-align: center;
        @include media($lg) {
            font-size: 32px;
            line-height: 38px;
        }
        @include media($xl) {
            font-size: 42px;
            line-height: 50px;
        }
    }

    .play-box__answers--left {
        color: $hsl-color-green-58;
        margin-left: 20px;
        @include media($lg) {
            margin-left: 35px;
        }
    }

    .play-box__answers--right {
        color: $hsl-color-green-58;
        margin-right: 20px;
        @include media($lg) {
            margin-right: 35px;
        }
    }

    .play-box__image {
        max-width: 100%;
        margin-bottom: 40px;
        @media screen and (max-width: 1200px) {
            height: 400px;
        }
    }

    .play-box__game-content {
        display: flex;
        width: 100%;
        @media screen and (max-width: 800px) {
            flex-direction: column-reverse;
            align-items: center;
            height: fit-content;
        }
        .game-info__block {
            display: flex;
            flex-direction: column;
            @media screen and (max-width: 800px) {
                &::v-deep .game-info {
                    width: 100%;
                }
                &::v-deep .game-info__block {
                    width: 100%;
                    flex-direction: row;
                    justify-content: center;
                    flex-wrap: wrap;
                    column-gap: 10px;
                }
            }
        }
    }

    .answer-button {
        margin-left: 10px;
        margin-right: 10px;
        height: 40px;
        @include media($sm) {
            height: auto;
        }
        &__mt {
            margin-top: 20px;
        }
    }

    .hint-button {
        margin-left: 10px;
        margin-right: 10px;
        margin-bottom: 15px;
    }
</style>
